import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';

import {
    EventInfoWithPlayersQuery,
    PlayerInfoFragment,
    useEventInfoWithPlayersQuery,
} from '@/api/events/query.generated';
import { EventInfo, EventWithoutTeams, isIndividualEvent } from '@/api/events/types/types';
import PlayIcon from '@/assets/icons/play';
import { EventTime } from '@/components/EventTime';
import { LiveEventStatus } from '@/components/LiveEventStatus';
import { SizedBox } from '@/components/SizedBox';
import { TeamScore } from '@/components/TeamScore';
import { Text } from '@/components/TextComponent';
import { HudEvent } from '@/components/event-hud/EventHudDetails';
import { getTeamColors } from '@/components/event-hud/utils';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { EventStatus } from '@/feature/bets-sbk/hooks/types';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { PICKS_USE_EVENT_STALE_TIME, useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { Event } from '@/feature/event-details-sbk/types';
import { convertPickemEventToHudEvent } from '@/feature/fantasy-event-sheets/components/PickemEventHud';
import { Tag } from '@/feature/lobby/components/EventHeader';
import { EventWidgetImageContainer } from '@/feature/player-profile-sheets/components/EventWidgetImageContainer';
import { PlayerProfileSheetPlaceholder } from '@/feature/player-profile-sheets/components/PlayerProfileSheetPlaceholder';
import {
    PLAYER_PROFILE_EVENT_WIDGET_PLACEHOLDER_SIZE,
    getPlayerNameInfo,
} from '@/feature/player-profile-sheets/utils/player-profile-utils';
import { useEventSupportsScoreboard } from '@/hooks/use-event-supports-scoreboard';
import { useEventStatus } from '@/hooks/use-sbk-event-details-cache';
import { common, designSystem } from '@/styles/styles';
import { EventAttributesType, EventStatus as EventStatuses } from '@/types/api.generated';
import { findEventAttribute } from '@/utils/fantasy-attribute-utils';
import { gameUtils } from '@/utils/games';
import { isPGA, isUFC } from '@/utils/league';
import { isPlayerImgPlaceholder } from '@/utils/player';
import { renderLongDayMonthDate, renderTime } from '@/utils/renderDate';

const BORDER_WIDTH = 1;

export const PlayerProfileEventWidget = ({
    event: pickemEvent,
    player,
}: {
    event?: EventInfo;
    player: PlayerWithTeamAndEvent;
}) => {
    const pamEventId = pickemEvent?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id;
    const showScoreboard = useEventSupportsScoreboard(pickemEvent);
    const liveEventWithScoreboard = pamEventId && showScoreboard;

    const hudEvent = useMemo(() => convertPickemEventToHudEvent(pickemEvent), [pickemEvent]);

    if (isIndividualEvent(pickemEvent)) {
        return <PlayerProfileIndividualEventWidget event={pickemEvent} player={player} />;
    }

    return !hudEvent ? null : liveEventWithScoreboard ? (
        <PlayerProfileLiveEventWidget hudEvent={hudEvent} pamEventId={pamEventId} />
    ) : (
        <PlayerProfileEventWidgetDetails hudEvent={hudEvent} />
    );
};

const PlayerProfileEventWidgetDetails = ({
    hudEvent,
    pamEvent,
    eventStatus,
}: {
    hudEvent: HudEvent;
    pamEvent?: Event;
    eventStatus?: EventStatus;
}) => {
    const { awayTeam, homeTeam } = hudEvent;
    const { home, away } = getTeamColors(hudEvent);
    const isFinished = eventStatus === 'FINISHED';
    const isLive = eventStatus === 'LIVE';

    return (
        <Wrapper style={[styles.teamEventContainer, common.spaceBetweenRow]}>
            <Row flexShrink={1}>
                <Box alignItems={'center'} minWidth={44} maxWidth={isLive ? 60 : 100}>
                    <EventWidgetImageContainer
                        primaryColor={away.primary}
                        secondaryColor={away.secondary}
                        shouldShowFallbackLogo={away.shouldShowFallbackLogo}
                        imageUrl={awayTeam?.logoUrl}
                        width={32}
                        height={32}
                    />
                    <NameLabel name={awayTeam?.shortCode} />
                </Box>
            </Row>
            {pamEvent ? (
                <>
                    <Box alignItems={'center'} flexShrink={1} minWidth={44}>
                        <TeamScore
                            event={pamEvent}
                            side="away"
                            fontSize={24}
                            lineHeight={24}
                            fontFamily="Gamay-CondSemiBold"
                        />
                    </Box>
                    <Box alignItems={'center'} flexShrink={1}>
                        <EventTime
                            event={pamEvent}
                            variant="labelLarge"
                            color={
                                isLive
                                    ? ds.colorToken.text.danger
                                    : isFinished
                                    ? ds.colorToken.text.secondary
                                    : ds.colorToken.text.primary
                            }
                        />
                        {pamEvent ? <LiveEventStatus event={pamEvent} color="neutral300" variant="bodySmall" /> : null}
                    </Box>
                    <Box alignItems={'center'} flexShrink={1} minWidth={44}>
                        <TeamScore
                            event={pamEvent}
                            side="home"
                            fontSize={24}
                            lineHeight={24}
                            fontFamily="Gamay-CondSemiBold"
                        />
                    </Box>
                </>
            ) : (
                <Box flex={1} alignItems={'center'}>
                    <TimeDetails startTime={hudEvent.startTime} />
                </Box>
            )}
            <Row flexShrink={1} maxWidth={isLive ? 60 : 100}>
                <Box alignItems={'center'} minWidth={44}>
                    <EventWidgetImageContainer
                        imageUrl={homeTeam?.logoUrl}
                        primaryColor={home.primary}
                        secondaryColor={home.secondary}
                        shouldShowFallbackLogo={home.shouldShowFallbackLogo}
                        width={32}
                        height={32}
                    />
                    <NameLabel name={homeTeam?.shortCode} />
                </Box>
            </Row>
        </Wrapper>
    );
};

const PlayerProfileLiveEventWidget = ({ pamEventId, hudEvent }: { pamEventId: string; hudEvent: HudEvent }) => {
    const { data: event } = useEvent(pamEventId, { staleTime: PICKS_USE_EVENT_STALE_TIME });
    const eventStatus = useEventStatus(pamEventId) ?? event?.status;
    return event?.status === 'LIVE' ? (
        <PlayerProfileEventWidgetDetails hudEvent={hudEvent} pamEvent={event} eventStatus={eventStatus} />
    ) : (
        <PlayerProfileEventWidgetDetails hudEvent={hudEvent} />
    );
};

const NameLabel = ({ name }: { name?: string }) => (
    <Box marginTop={'s2'} flex={1}>
        <Text variant={'bodySmaller'} lineHeight={16} color={'neutral300'} style={styles.name} numberOfLines={1}>
            {name}
        </Text>
    </Box>
);

const PlayerProfileIndividualEventWidget = ({
    event,
    player,
}: {
    event: EventWithoutTeams;
    player: PlayerWithTeamAndEvent;
}) => {
    const { t } = useTranslation('sports_events');
    const isPgaEvent = isPGA(event?.league);
    const isUfcEvent = isUFC(event?.league);
    const isLive = isPgaEvent ? event.status === EventStatuses.InProgress : gameUtils.isLive(event);

    const [{ data }] = useEventInfoWithPlayersQuery({
        variables: { id: event.id ?? '' },
        // pause this query if the event has more than one player or if it's not a UFC event
        // this is necessary for UFC because we don't have both players from favorites for example and they are both displayed here
        pause: !isUFC(event?.league) || event.players?.length > 1,
    });

    const eventWithPlayers = useMemo<EventInfoWithPlayersQuery['getEventByIdV2'] | undefined>(
        () => (data?.getEventByIdV2 ? data.getEventByIdV2 : undefined),
        [data?.getEventByIdV2]
    );

    if (isPgaEvent) {
        const date = gameUtils.createTimeLabel(event);
        const startDate = findEventAttribute(event, EventAttributesType.TournamentStartDateTime) ?? '';
        const endDate = findEventAttribute(event, EventAttributesType.TournamentEndDateTime) ?? '';
        const dateRanges = gameUtils.formatTournamentDates(startDate, endDate);
        const roundNumber = findEventAttribute(event, EventAttributesType.RoundNumber) ?? '';

        return (
            <Wrapper style={styles.pgaWrapper}>
                <Box flex={1} mr={'s4'}>
                    <Text variant={'titleMedium'}>{event.name}</Text>
                    <Text variant={'bodySmall'} color={'neutral300'}>
                        {date !== '' ? date : dateRanges}
                    </Text>
                </Box>
                <Row gap={'s8'}>
                    <Tag label={`${t('round')} ${roundNumber}`} color={ds.colorToken.background.neutral.stronger} />
                    {isLive ? (
                        <Tag
                            label="Live"
                            color={ds.colorToken.text.danger}
                            icon={<PlayIcon />}
                            labelColor="neutral900"
                        />
                    ) : null}
                </Row>
            </Wrapper>
        );
    } else if (isUfcEvent) {
        const individualEventPlayers =
            eventWithPlayers && isIndividualEvent(eventWithPlayers) ? eventWithPlayers.players : undefined;
        const players = event.players.length > 1 || !isUFC(event?.league) ? event.players : individualEventPlayers;
        const opponent = players?.length !== 2 ? null : players.find(ufcPlayer => ufcPlayer.id !== player?.id);
        const isPlaceholderImageOpponentPlayer = isPlayerImgPlaceholder(opponent?.icon);
        const isPlaceholderImagePlayer = isPlayerImgPlaceholder(player?.icon);
        return (
            <Wrapper style={styles.ufcWrapper}>
                {isPlaceholderImageOpponentPlayer && opponent ? (
                    <PlayerImageOrPlaceholder player={opponent} isPlaceholder={isPlaceholderImageOpponentPlayer} />
                ) : opponent ? (
                    <PlayerImageOrPlaceholder player={opponent} />
                ) : (
                    /* rare case - to fill the empty space that is visible for the opponent */
                    <SizedBox value={50} />
                )}
                <TimeDetails startTime={event.date} />
                {isPlaceholderImagePlayer ? (
                    <PlayerImageOrPlaceholder player={player} isPlaceholder={isPlaceholderImagePlayer} />
                ) : (
                    <PlayerImageOrPlaceholder player={player} />
                )}
            </Wrapper>
        );
    }
    return null;
};

export const PlayerImageOrPlaceholder = ({
    player,
    isPlaceholder = false,
    size = 40,
}: {
    player: PlayerInfoFragment;
    isPlaceholder?: boolean;
    size?: number;
}) => {
    const { mainName } = getPlayerNameInfo(player);

    return (
        <Box alignItems="center">
            {isPlaceholder ? (
                <PlayerProfileSheetPlaceholder
                    opacity={0.4}
                    background={designSystem.colors.neutral800}
                    width={PLAYER_PROFILE_EVENT_WIDGET_PLACEHOLDER_SIZE.width}
                    height={PLAYER_PROFILE_EVENT_WIDGET_PLACEHOLDER_SIZE.height}
                />
            ) : (
                <EventWidgetImageContainer imageUrl={player.icon} width={size} height={size} shouldApplyMask />
            )}
            <NameLabel name={mainName} />
        </Box>
    );
};

const Wrapper = ({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) => {
    return (
        <Row
            backgroundColor={ds.colorToken.background.neutral.subtler}
            borderRadius={'r16'}
            borderWidth={BORDER_WIDTH}
            borderColor={ds.colorToken.border.subtle}
            width={'100%'}
            alignItems={'center'}
            style={[styles.eventHudContainer, style]}
        >
            {children}
        </Row>
    );
};

const TimeDetails = ({ startTime }: { startTime: number | string }) => {
    return (
        <Box alignItems={'center'}>
            <Text variant={'bodySmaller'} lineHeight={16} color={'neutral300'}>
                {renderLongDayMonthDate(new Date(startTime))}
            </Text>
            <Text variant={'bodySmaller'} lineHeight={16} color={'neutral300'}>
                {renderTime(new Date(startTime))}
            </Text>
        </Box>
    );
};

export const styles = StyleSheet.create({
    eventHudContainer: {
        borderCurve: 'continuous',
        padding: 10 - BORDER_WIDTH,
    },
    name: {
        fontWeight: '500',
    },
    pgaWrapper: {
        paddingHorizontal: 16,
        justifyContent: 'space-between',
    },
    ufcWrapper: {
        justifyContent: 'space-evenly',
    },
    teamEventContainer: {
        paddingHorizontal: 19,
    },
});
